import React from 'react';
import styled from 'styled-components';

import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import PropTypes from 'prop-types';

import { dig, blank } from 'spiceutils';
import { getSurveyForCdf, getCountriesForSurvey } from 'utils/AYDAUtils';

import {
  makeSelectQuestionSuggestions,
  makeSelectTemporaryControlList,
  makeSelectQuestionControlList,
  makeSelectShowNewQuestionSelector,
} from '../App/selectors';

import {
  getQuestionSuggestions,
  updateQuestionSuggestions,
  createQuestionControl,
  createTemporaryChartControl,
  storeTemporaryQuestionControlList,
  setShowNewQuestion,
  storeQuestionControlList,
  getPresetBreakList,
  getPresetCountryList,
  storeNewChart,
} from '../App/actions';

import Question from '../../components/FormControls/Question';
import KidAdultSelector from '../../components/FormControls/KidAdultSelector';
import Visualisation from '../../components/FormControls/Visualisation';
import Background from '../../components/Modal';
import Country from '../../components/FormControls/Country';

import close from '../../images/cancel.svg';
import Button from '../../components/Button';

// Styles
const ButtonsContainer = styled.div`
  text-align: right;
`;

const ModalWindow = styled.div`
  position: absolute;
  top: 10%;
  left:10%;
  background-color: #fff;
  width: 80%;
  height: 80%;
  z-index: 999;
  padding: 30px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,.5);
`;

const Header = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  padding: 0px;
  margin-bottom: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background-image: url(${close});
  background-repeat: no-repeat;
  background-size: 15px;
  background-position: center;
  width: 20px;
  height: 20px;
  filter: invert(100%);
`;

const Controls = styled.div`
  padding: 20px 0;
`;

const ViewChartButton = styled(Button)`
  margin-top: 20px;
  margin-left: 10px;
  float: right;
`;

const CancelButton = styled(Button)`
  margin-top: 20px;
  margin-left: 20px;
  float: right;
  background-color: #a2002d;
    &:hover{
      background-color: #b71845;
    }
`;

const StyledCountry = styled(Country)`
  label {
    font-family: ${(props) => props.theme.fontAvenirBold};
    font-size: ${(props) => props.theme.h4};
    margin-top: 20px;
  }
`;

class QuestionSelector extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      questionCode: '',
      kidAdult: '',
      visualisation: '',
      country: '',
      showKidAdultField: false,
      requiresCountry: false,
    };
  }
  componentDidMount() {
    this.props.initialize();
  }
  componentWillReceiveProps(newProps) {
    // If KidAdult control is defined
    if (dig(newProps.questionControlList, ['controlList', 'KidAdult', 'Name']) !== undefined) {
      const kidAdultControl = newProps.questionControlList.controlList.KidAdult;
      // If KidAdult control has multiple options (SingleOption is not set)
      if (blank(kidAdultControl.SingleOption)) {
        this.setState({ showKidAdultField: true });
      } else {
        this.setState({ showKidAdultField: false });
        this.storeKidAdult(kidAdultControl.SingleOption);
      }
    }

    // Determine whether or not the country can be automatically determined for the current
    // Question / Visualisation / Package. If so we can set it here.
    let visualisationOptions;
    let visualisationRequiresCountry = true;

    // Look at the options for the selected visualisation to determine if it requires a country.
    if (this.state.visualisation && dig(this.props.questionControlList, ['controlList'])) {
      // Get the options for all visualisations from the controlList
      const allVisOptions = this.props.questionControlList.controlList.VisualisationList.Options[`${this.state.kidAdult}List`];
      // Find the options for our visualisation
      visualisationOptions = allVisOptions.filter((vis) => (
        vis.name === this.state.visualisation
      ))[0];
      visualisationRequiresCountry = visualisationOptions === undefined ? false : visualisationOptions.required.includes('Country');
    }

    // Determine the countries for the question based on the controlList
    let survey;
    let countries = [];
    if (dig(newProps.controlList, ['Country', 'Visible'])) {
      survey = getSurveyForCdf(newProps.cdf);
      countries = getCountriesForSurvey(survey, newProps.controlList);
    }

    // We require a country if it is in the options and we have multiple countries to choose from
    const requiresCountry = visualisationRequiresCountry && !(countries.length === 1);

    this.setState({ requiresCountry });
    if (!requiresCountry && countries.length === 1) {
      this.setState({ country: countries[0].value });
    }
  }

  storeQuestionCode(code) {
    this.setState({ questionCode: code });
    if (code !== '' && this.props.questionControlList.questionCode !== code) {
      this.props.createQuestionControl(code);
    }
  }

  storeKidAdult(value) {
    this.setState({ kidAdult: value });
  }

  storeVisualisation(vis) {
    this.setState({ visualisation: vis });
    this.props.createChartControl(vis, this.state.questionCode, this.state.kidAdult === 'kid');
  }

  storeCountry(_field, country) {
    if (country && country !== 'None') {
      this.setState({ country });
    } else {
      this.setState({ country: '' });
    }
  }

  build() {
    const {
      clientPackage,
      controlList,

      close,
      getPresetBreakLists,
      getPresetCountryLists,
      storeNewChart,
    } = this.props;

    const {
      country,
      kidAdult,
      questionCode,
      visualisation,
    } = this.state;

    const newCDF = {
      QuestionCode: questionCode,
      Visualisation: visualisation,
      Package: clientPackage,
      Adults: kidAdult === 'adult',
    };
    if (country !== '') {
      newCDF.Country = country;
      if (controlList.BreakList.Visible === true) {
        getPresetBreakLists(country, controlList, blank(newCDF.Adults) ? false : !newCDF.Adults);
      }
    }
    storeNewChart(newCDF, controlList);

    if (dig(controlList, ['CountryList', 'Visible']) === true) {
      getPresetCountryLists();
    }
    close();
  }
  render() {
    const controls = [];

    controls.push(
      <Question
        getSuggestions={this.props.getQuestionSuggestions}
        suggestions={this.props.questionSuggestions}
        storeQuestionCode={(c) => this.storeQuestionCode(c)}
        storedQuestionCode={this.state.questionCode}
        createQuestionControl={this.props.createQuestionControl}
        key="Question"
      />
    );

    if (this.state.questionCode !== '' && this.state.showKidAdultField) {
      // Push Kid/Adult selector to controls
      controls.push(
        <KidAdultSelector
          kidAdult={this.state.kidAdult}
          storeKidAdult={(value) => this.storeKidAdult(value)}
          key="KidAdult"
        />
      );
    }

    if (this.state.kidAdult !== '' && this.props.questionControlList.controlList) {
      controls.push(
        <Visualisation
          controlList={this.props.questionControlList.controlList.VisualisationList}
          storeVisualisation={(v) => this.storeVisualisation(v)}
          storedVisualisation={this.state.visualisation}
          kidAdult={this.state.kidAdult}
          key="Visualisation"
        />
      );
    }

    if (this.state.questionCode !== ''
    && this.state.kidAdult !== ''
    && this.state.visualisation !== ''
    && this.props.questionControlList.controlList
    && this.state.requiresCountry) {
      controls.push(
        <StyledCountry
          updateCDF={(f, country) => this.storeCountry(f, country)}
          controlList={this.props.controlList}
          cdf={{ Country: this.state.country }}
          storePresetLists={false}
          key="Country"
        />
      );
    }

    if (this.state.questionCode !== ''
    && this.state.kidAdult !== ''
    && this.state.visualisation !== ''
    && this.props.questionControlList.controlList
    && (!this.state.requiresCountry || this.state.country !== '')) {
      controls.push(
        <ButtonsContainer key="Buttons">
          <ViewChartButton onClick={() => { this.build(); }} key="Button">View Chart</ViewChartButton>
          {this.props.showNewQuestionSelector && (<CancelButton onClick={this.props.close}>Cancel</CancelButton>)}
        </ButtonsContainer>
      );
    }

    return (
      <div>
        <Background onClick={this.props.close} />
        <ModalWindow>
          <Header>
            <Title>Select a question to visualise</Title>
            {this.props.showNewQuestionSelector && (<CloseButton onClick={this.props.close} />)}
          </Header>
          <Controls>
            {controls}
          </Controls>
        </ModalWindow>
      </div>
    );
  }
}

export function mapDispatchToProps(dispatch) {
  return {
    initialize: () => {
      dispatch(updateQuestionSuggestions([]));
      dispatch(storeQuestionControlList({}));
    },
    getQuestionSuggestions: (text) => {
      if (text && text.length > 0) {
        dispatch(getQuestionSuggestions(text));
      } else {
        dispatch(updateQuestionSuggestions([]));
      }
    },
    createQuestionControl: (code) => {
      dispatch(createQuestionControl(code));
    },
    createChartControl: (visId, code, kidsWave) => {
      dispatch(createTemporaryChartControl(visId, code, kidsWave));
    },
    close: () => {
      dispatch(setShowNewQuestion(false));
      dispatch(storeTemporaryQuestionControlList({}));
    },
    getPresetBreakLists(value, controlList, kidsWave) {
      dispatch(getPresetBreakList(value, controlList, kidsWave));
    },
    getPresetCountryLists() {
      dispatch(getPresetCountryList());
    },
    storeNewChart(cdf, controlList) {
      dispatch(storeNewChart(cdf, controlList));
    },
  };
}

const mapStateToProps = createStructuredSelector({
  questionSuggestions: makeSelectQuestionSuggestions(),
  questionControlList: makeSelectQuestionControlList(),
  controlList: makeSelectTemporaryControlList(),
  showNewQuestionSelector: makeSelectShowNewQuestionSelector(),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

QuestionSelector.propTypes = {
  questionControlList: PropTypes.object,
  createQuestionControl: PropTypes.func,
  clientPackage: PropTypes.string,
  getQuestionSuggestions: PropTypes.func,
  questionSuggestions: PropTypes.array,
  createChartControl: PropTypes.func,
  storeNewChart: PropTypes.func,
  initialize: PropTypes.func,
  close: PropTypes.func,
  controlList: PropTypes.object,
  showNewQuestionSelector: PropTypes.bool,
  getPresetBreakLists: PropTypes.func,
  getPresetCountryLists: PropTypes.func,
};

export default compose(
  withConnect
)(QuestionSelector);



// WEBPACK FOOTER //
// ./app/containers/QuestionSelector/index.js